/**
 * Главный экран `/brands` (§4.1).
 *
 * Вертикальная раскладка: строка управления 36 (`FilterBar`) → строка чипов 32
 * (`ChipRow`) → строка активных пилюль 30 (`ActivePills`, появляется только при
 * активных фильтрах или расхождении снимка) → тело таблицы → панель массовых
 * операций. Карточка бренда — вложенный маршрут `/brands/:slug`: она выезжает
 * панелью поверх таблицы, поэтому рендерится через `Outlet`.
 *
 * Разделение с волной фильтров: оттуда приезжают состояние (`useBrandFilters`)
 * и три полосы хрома, отсюда — набор колонок, снимок строк и правка по месту.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Outlet, useMatch, useNavigate, useSearchParams } from 'react-router-dom'
import type { Brand } from '@shared/types.ts'
import { Button, EmptyState, Modal, Skeleton } from '../../components/index.js'
import { api } from '../../lib/api.js'
import { useApp } from '../../store/app-store.js'
import { ColumnsMenu } from './ColumnsMenu.js'
import { BulkBar } from './editing/BulkBar.js'
import { useBulkEdit } from './editing/useBulkEdit.js'
import { useRowSnapshot } from './editing/useRowSnapshot.js'
import {
  ActivePills, FilterBar, FilterPills, missingCount, useBrandFilters,
  type FilterContext,
} from './filters/index.js'
import { BrandTable } from './table/BrandTable.js'
import { TableSearch } from './table/TableSearch.js'
import type { TableLayout } from './table/BrandRow.js'
import {
  buildColumnCatalog, DEFAULT_COLLAPSED_GROUPS, defaultVisible, resolveColumns,
  type ColumnGroupCode,
} from './table/columns.js'
import {
  getColumnOrder, resetColumnOrder, setColumnOrderIn, setGroupOrder, subscribeColumnOrder,
} from './table/columnOrder.js'
import { useIsMobile } from '../../app/useIsMobile.js'
import { BrandCards } from './BrandCards.js'
import { AddBrandDialog } from './card/AddBrandDialog.js'
import { Copilot } from '../copilot/Copilot.js'
import { writeCell, type ValueContext, type WriteActions } from './table/values.js'
import { tableTexts as t } from './texts.table.js'
import './BrandsScreen.css'

/**
 * Строка управления экрана живёт в шапке приложения: поиск, канал, сортировка
 * и фильтры стоят в одну строку. Пока слота нет (первый кадр) — рисуем на месте.
 */
function ToolbarSlot({ children }: { children: React.ReactNode }) {
  const [host, setHost] = useState<HTMLElement | null>(null)
  useEffect(() => {
    setHost(document.getElementById('bm-toolbar'))
  }, [])
  return host ? createPortal(children, host) : <>{children}</>
}

export function BrandsScreen() {
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()

  const status = useApp((s) => s.status)
  const bootstrap = useApp((s) => s.bootstrap)
  const brandsById = useApp((s) => s.brandsById)
  const overlay = useApp((s) => s.overlay)
  const conflictList = useApp((s) => s.conflicts)
  const revision = useApp((s) => s.revision)
  const toast = useApp((s) => s.toast)
  const reloadBootstrap = useApp((s) => s.reloadBootstrap)
  const editBrand = useApp((s) => s.editBrand)
  const editChannel = useApp((s) => s.editChannel)
  const editCategoryChannel = useApp((s) => s.editCategoryChannel)

  const state = useBrandFilters()
  const { channel, filters, sort, visibleBrands, totalBrands, staleBrandIds } = state

  // ── геометрия: доступная ширина тела таблицы (§4.2, §4.8) ─────────────────
  const [area, setArea] = useState<HTMLDivElement | null>(null)
  const [available, setAvailable] = useState(0)
  useEffect(() => {
    if (!area || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(() => setAvailable(area.clientWidth))
    observer.observe(area)
    setAvailable(area.clientWidth)
    return () => observer.disconnect()
  }, [area])

  // ── набор колонок (§4.2, §4.4, §4.9) ──────────────────────────────────────
  const fields = useMemo(() => bootstrap?.fields ?? [], [bootstrap])
  /* Переименования колонок и блоков живут в общих настройках: таблица одна
     на всех, и заголовки в ней тоже общие. */
  const columnTitles = bootstrap?.settings.columnTitles
  const groupTitleMap = bootstrap?.settings.groupTitles
  const movedColumns = bootstrap?.settings.columnGroups
  const baseCatalog = useMemo(
    () => buildColumnCatalog(channel, fields, {
      columns: columnTitles, groups: groupTitleMap, moved: movedColumns,
    }),
    [channel, fields, columnTitles, groupTitleMap, movedColumns],
  )

  /* Ширины, выставленные человеком перетаскиванием границы заголовка.
     Помним между заходами: ширина колонки — личная привычка. */
  const [widths, setWidths] = useState<Record<string, number>>(() => {
    try {
      const stored = localStorage.getItem('bm.colWidths')
      return stored ? (JSON.parse(stored) as Record<string, number>) : {}
    } catch {
      return {}
    }
  })

  const resizeColumn = useCallback((code: string, width: number) => {
    setWidths((previous) => {
      const next = { ...previous, [code]: Math.round(width) }
      try {
        localStorage.setItem('bm.colWidths', JSON.stringify(next))
      } catch {
        /* приватный режим — ширины просто не переживут перезагрузку */
      }
      return next
    })
  }, [])

  /* Колонка названия обнимает содержимое: ширина считается по самому длинному
     названию бренда в срезе, а не берётся «на глаз». */
  const brandWidth = useMemo(() => {
    const names = [...brandsById.values()].map((item) => item.name)
    if (names.length === 0) return 170
    const canvas = document.createElement('canvas')
    const context = canvas.getContext('2d')
    if (!context) return 170
    const styles = getComputedStyle(document.documentElement)
    const size = styles.getPropertyValue('--fs-brand').trim() || '21px'
    context.font = `700 ${size} 'Aeonik Pro', sans-serif`
    /* Ширину задаёт не самое длинное название, а 90-й процентиль: из-за одного
       «Fratelli Onofri» колонка раздувалась, а 90 % строк оставались с пустотой.
       Длинные названия обрезаются многоточием. */
    const widthsPx = names.map((name) => context.measureText(name).width).sort((a, b) => a - b)
    const p90 = widthsPx[Math.floor(widthsPx.length * 0.9)] ?? widthsPx[widthsPx.length - 1] ?? 120
    return Math.round(Math.min(200, Math.max(120, p90 + 52)))
  }, [brandsById])

  const catalog = useMemo(
    () => baseCatalog.map((column) => {
      const manual = widths[column.code]
      if (manual) return { ...column, width: manual, flex: undefined }
      if (column.code === 'brand') return { ...column, width: brandWidth, flex: undefined }
      return column
    }),
    [baseCatalog, brandWidth, widths],
  )

  const userArea = bootstrap?.user.area ?? null
  const areaFields = state.context.areaFields ?? []

  const [collapsedGroups, setCollapsedGroups] = useState<ReadonlySet<ColumnGroupCode>>(
    () => new Set(DEFAULT_COLLAPSED_GROUPS),
  )
  const [pinnedLeftExtra, setPinnedLeftExtra] = useState<string | null>(null)

  /* Личный порядок колонок живёт вне React (localStorage), поэтому экран на
     него подписывается: перетащили — пересчитали раскладку. */
  /* Панель фильтров закрыта, пока её не открыли кнопкой. Если фильтр уже
     стоит (пришли по ссылке), панель открывается сама: иначе непонятно,
     почему брендов меньше, чем всего. */
  /* На телефоне таблица превращается в список карточек: тринадцать колонок на
     экране в 375 px не читаются. */
  const mobile = useIsMobile()

  /* Какой бренд открыт в карточке: его строку таблица подсвечивает. */
  const openMatch = useMatch('/brands/:slug')
  const openSlug = openMatch?.params.slug ?? null

  const [filtersOpen, setFiltersOpen] = useState(() => state.activeCount > 0)
  const [addingBrand, setAddingBrand] = useState(false)

  const [order, setOrder] = useState(getColumnOrder)
  useEffect(() => subscribeColumnOrder(() => setOrder(getColumnOrder())), [])

  const resolved = useMemo(() => resolveColumns({
    channel, catalog, chosen: state.columns, collapsedGroups,
    areaFields, area: userArea, pinnedLeftExtra, available, order,
  }), [channel, catalog, state.columns, collapsedGroups, areaFields, userArea, pinnedLeftExtra, available, order])

  const layout = useMemo<TableLayout>(() => {
    const pinnedLeft = resolved.columns.filter((column) => column.pinned === 'left')
    const pinnedRight = resolved.columns.filter((column) => column.pinned === 'right')
    return {
      columns: resolved.columns,
      catalog,
      leftOffset: resolved.leftOffset,
      rightOffset: resolved.rightOffset,
      lastLeftCode: pinnedLeft[pinnedLeft.length - 1]?.code ?? null,
      firstRightCode: pinnedRight[0]?.code ?? null,
      totalWidth: resolved.totalWidth,
    }
  }, [resolved, catalog])

  const setUrlKey = useCallback((key: string, value: string | null) => {
    const search = new URLSearchParams(params)
    if (value === null || value === '') search.delete(key)
    else search.set(key, value)
    setParams(search, { replace: true })
  }, [params, setParams])

  const columnsBase = useCallback(
    () => state.columns ?? defaultVisible(channel, catalog),
    [state.columns, channel, catalog],
  )

  // ── раскрытие категорий в URL (§4.3, §10.5) ───────────────────────────────
  const expanded = useMemo(() => {
    const raw = params.get('open')
    if (!raw) return new Set<number>()
    return new Set(raw.split(',').map(Number).filter((id) => Number.isFinite(id)))
  }, [params])

  const toggleExpanded = useCallback((brandId: number) => {
    const next = new Set(expanded)
    if (next.has(brandId)) next.delete(brandId)
    else next.add(brandId)
    setUrlKey('open', [...next].join(','))
  }, [expanded, setUrlKey])

  // ── снимок строк (§5.4) ───────────────────────────────────────────────────
  const signature = useMemo(
    () => `${channel}|${JSON.stringify(filters)}|${JSON.stringify(sort)}`,
    [channel, filters, sort],
  )
  const snapshot = useRowSnapshot({
    visibleBrands, signature, revision,
    draftBrandIds: overlay.draftBrands, brandsById, driftIds: staleBrandIds,
  })

  // ── контекст значений, права, конфликты ───────────────────────────────────
  const ctx = useMemo<ValueContext>(() => ({
    overlay,
    channel,
    enums: bootstrap?.enums ?? {},
    users: bootstrap?.users ?? [],
  }), [overlay, channel, bootstrap])

  const permissions = bootstrap?.user.permissions ?? []
  const perms = useMemo(() => {
    if (permissions.includes('brands.edit')) return { mayEdit: true, onlyEmpty: false, reason: '' }
    if (permissions.includes('brands.create')) {
      return { mayEdit: true, onlyEmpty: true, reason: t.denied.contributorFilled }
    }
    return { mayEdit: false, onlyEmpty: false, reason: t.denied.viewer }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissions.join(',')])

  const conflicts = useMemo(
    () => new Map(conflictList.map((conflict) => [conflict.changeId, conflict])),
    [conflictList],
  )

  const writeActions = useMemo<WriteActions>(
    () => ({ editBrand, editChannel, editCategoryChannel }),
    [editBrand, editChannel, editCategoryChannel],
  )

  /** «Пропущено» считает ядро фильтров — тем же счётом, что чип «Мои пропуски». */
  const missingContext = useMemo<FilterContext>(
    () => ({ ...state.context, overlay }),
    [state.context, overlay],
  )
  const missingOf = useCallback(
    (brand: Brand) => missingCount(brand, channel, missingContext),
    [channel, missingContext],
  )

  // ── массовые операции (§5.7) ──────────────────────────────────────────────
  const bulk = useBulkEdit({
    shownBrands: snapshot.brands,
    ctx,
    write: (brand, column, value) => writeCell(
      writeActions, channel, column, { kind: 'brand', key: `b${brand.id}`, brand }, value,
    ),
  })

  const channelName = bootstrap?.channels.find((item) => item.code === channel)?.name ?? ''

  const [expandAllAsk, setExpandAllAsk] = useState(false)
  const expandAllCost = useMemo(() => {
    let rows = 0
    for (const brand of snapshot.brands) rows += Math.max(brand.categories.length, 1)
    return { brands: snapshot.brands.length, rows }
  }, [snapshot.brands])

  const openBrandId = useMemo(
    () => (openSlug ? snapshot.brands.find((brand) => brand.slug === openSlug)?.id ?? null : null),
    [openSlug, snapshot.brands],
  )

  // Ctrl+Enter открывает ревью из любого места (§5.3).
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Enter' || !(event.ctrlKey || event.metaKey)) return
      event.preventDefault()
      navigate('/draft')
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [navigate])

  /* Перенос колонки в другой блок — общая настройка: таблица одна на всех,
     и её устройство тоже. Личный порядок внутри блока остаётся личным. */
  const moveColumnToGroup = useCallback(async (code: string, group: string) => {
    try {
      await api.renameTableTitle({ kind: 'move', code, title: group })
      await reloadBootstrap()
    } catch (err) {
      toast('error', err instanceof Error ? err.message : t.columnSettings.failed)
    }
  }, [reloadBootstrap, toast])

  const openCard = useCallback((brand: Brand, tab?: string) => {
    const query = new URLSearchParams({ ch: channel })
    if (tab) query.set('tab', tab)
    navigate(`/brands/${brand.slug}?${query.toString()}`)
  }, [navigate, channel])

  const columnsMenu = useCallback(
    (args: { anchor: HTMLElement | null; open: boolean; onClose: () => void }) => (
      <ColumnsMenu
        open={args.open}
        anchor={args.anchor}
        onClose={args.onClose}
        onMoveColumn={moveColumnToGroup}
        catalog={catalog}
        visible={resolved.wanted}
        hiddenByWidth={resolved.hiddenByWidth}
        collapsedGroups={collapsedGroups}
        onToggleColumn={(code, on) => {
          const base = columnsBase()
          setUrlKey('cols', (on ? [...base, code] : base.filter((item) => item !== code)).join(','))
        }}
        onToggleGroup={(group) => setCollapsedGroups((previous) => toggled(previous, group))}
        onReset={() => {
          setUrlKey('cols', null)
          resetColumnOrder()
        }}
        groupOrder={order.groups}
        onGroupOrder={setGroupOrder}
        columnOrder={order.columns}
        onColumnOrder={setColumnOrderIn}
        onFieldCreated={(code) => {
          /* Новый столбец сразу показываем: иначе человек создал его и не
             видит, куда он делся. Колонка пользовательского поля называется
             `field:<код>` — голый код таблица не узнает. */
          setUrlKey('cols', [...columnsBase(), `field:${code}`].join(','))
        }}
      />
    ),
    [catalog, resolved.wanted, resolved.hiddenByWidth, collapsedGroups, columnsBase, setUrlKey, order],
  )

  // ── §11.1 Загрузка: хром настоящий, тело — статичный скелетон ─────────────
  if (status !== 'ready' || !bootstrap) {
    return (
      <div className="brands">
        <div className="brands__area">
          <Skeleton
            rows={12}
            columns={resolved.columns.map((column) => column.width)}
            rowHeight="brand"
            label={t.loading.label}
          />
        </div>
        <Outlet />
      </div>
    )
  }

  const hasQuery = (filters.query ?? '') !== ''

  /* Одно и то же пустое состояние: на телефоне и при пустом канале — вместо
     списка, а в таблице — под её шапкой, чтобы каркас остался на месте. */
  const emptyState = (
    <EmptyState
      title={
        totalBrands === 0
          ? t.state.channelEmptyTitle
          : hasQuery ? t.state.searchZeroTitle : t.state.filtersZeroTitle
      }
      text={
        totalBrands === 0
          ? undefined
          : hasQuery
            ? t.state.searchZeroText(totalBrands, channelName)
            : t.state.filtersZeroText(totalBrands, channelName)
      }
      actions={totalBrands === 0 ? null : (
        <Button
          variant="secondary"
          size="m"
          onClick={() => {
            if (!hasQuery) { state.clearAll(); return }
            const next = { ...filters }
            delete next.query
            state.setFilters(next)
          }}
        >
          {hasQuery ? t.state.searchZeroAction : t.state.clearAll}
        </Button>
      )}
    />
  )

  return (
    <div className="brands">
      <ToolbarSlot>
        <FilterBar
          state={state}
          canCreateBrand={permissions.includes('brands.create')}
          onCreateBrand={() => setAddingBrand(true)}
          renderColumnsMenu={columnsMenu}
          filtersOpen={filtersOpen}
          onToggleFilters={() => setFiltersOpen((open) => !open)}
          columnsCustom={state.columns !== null}
          onResetColumns={() => { setUrlKey('cols', null); resetColumnOrder() }}
        />
      </ToolbarSlot>
      <AddBrandDialog
        open={addingBrand}
        onClose={() => setAddingBrand(false)}
      />

      {/* Копилот: шарик в правом нижнем углу, окно раскрывается над ним. */}
      <Copilot />

      {/* Панель фильтров: показывается кнопкой «Фильтры» в шапке. Строка
          активных пилюль под ней не нужна — выбранное видно в самих чипах;
          `ActivePills` остаётся только ради сообщения о расхождении снимка. */}
      {filtersOpen ? <FilterPills state={state} /> : null}
      <ActivePills state={state} showPills={false} />

      <div className="brands__area" ref={setArea}>
        {/* Когда под фильтр ничего не подошло, каркас таблицы остаётся на
            месте: исчезали шапка групп и заголовки колонок, и человек терял
            контекст — какие колонки он смотрел и что вообще отфильтровано.
            Пустая таблица со своей шапкой отвечает на это сама. */}
        {snapshot.brands.length === 0 && (totalBrands === 0 || mobile) ? (
          emptyState
        ) : mobile ? (
          <BrandCards
            brands={snapshot.brands}
            channel={channel}
            overlay={overlay}
            labelOf={(set, value) => {
              if (value === null) return null
              const list = bootstrap?.enums[set] ?? []
              return list.find((item) => item.value === value)?.label ?? value
            }}
            colorOf={(set, value) => {
              if (value === null) return null
              const list = bootstrap?.enums[set] ?? []
              return list.find((item) => item.value === value)?.color ?? null
            }}
          />
        ) : (
          <BrandTable
            openBrandId={openBrandId}
            brands={snapshot.brands}
            empty={emptyState}
            search={(
              <TableSearch
                value={filters.query ?? ''}
                onChange={(query) => {
                  const next = { ...filters }
                  if (query.trim() === '') delete next.query
                  else next.query = query
                  state.setFilters(next)
                }}
              />
            )}
            layout={layout}
            ctx={ctx}
            overlay={overlay}
            perms={perms}
            conflicts={conflicts}
            revision={revision}
            channelName={channelName}
            missingOf={missingOf}
            myColumn={resolved.myColumn}
            sort={sort}
            onSort={(code, additive) => state.toggleColumnSort(code, additive)}
            collapsedGroups={collapsedGroups}
            onToggleGroup={(group) => setCollapsedGroups((previous) => toggled(previous, group))}
            expanded={expanded}
            onToggleExpanded={toggleExpanded}
            onExpandAll={() => setExpandAllAsk(true)}
            driftIds={snapshot.driftIds}
            outsideFilterIds={snapshot.outsideFilterIds}
            bulk={bulk}
            selectionEnabled={perms.mayEdit}
            writeActions={writeActions}
            onOpenComments={(brand) => openCard(brand, 'comments')}
            onSwitchChannel={(code) => state.setChannel(code)}
            onAddToChannel={(brand, code) => { void editChannel(brand.id, code, { isPresent: true }) }}
            onStatus={(text) => toast('info', text)}
            pinnedLeftExtra={pinnedLeftExtra}
            onResizeColumn={resizeColumn}
            onSortDirection={(code, direction) => {
              // Явный выбор из меню колонки: одна колонка, заданное направление.
              if (direction === null) state.setSort([])
              else state.setSort([{ column: code, direction }])
            }}
            onHideColumn={(code) => {
              setUrlKey('cols', columnsBase().filter((item) => item !== code).join(','))
            }}
          />
        )}
      </div>

      <BulkBar bulk={bulk} columns={resolved.columns} ctx={ctx} onDone={(text) => toast('success', text)} />

      <Modal
        open={expandAllAsk}
        onClose={() => setExpandAllAsk(false)}
        title={t.row.expandAllLabel}
        size="s"
        footer={(
          <>
            <Button variant="secondary" size="m" onClick={() => setExpandAllAsk(false)}>
              {t.bulk.cancel}
            </Button>
            <Button
              variant="primary"
              size="m"
              onClick={() => {
                setExpandAllAsk(false)
                setUrlKey('open', snapshot.brands.map((brand) => brand.id).join(','))
              }}
            >
              {t.row.expandAllLabel}
            </Button>
          </>
        )}
      >
        {t.hint.expandAll(expandAllCost.brands, expandAllCost.rows)}
      </Modal>

      <Outlet />
    </div>
  )
}

function toggled(
  previous: ReadonlySet<ColumnGroupCode>, group: ColumnGroupCode,
): ReadonlySet<ColumnGroupCode> {
  const next = new Set(previous)
  if (next.has(group)) next.delete(group)
  else next.add(group)
  return next
}
